import { IDropdownOption, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesGhost,
  buttonStylesPrimary,
  Dropdown,
  TextField,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { FormEvent, useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  CoreService,
  ScheduledTask,
  ScheduledTaskResponse,
  ScheduledTaskUpdateRequest,
  ScheduleTaskRequest,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { buttonStylesPrimaryPanel, ContentsTitle } from '../../featurestore/models';
import { getErrorMessageBarMessage } from '../../featurestore/utils';
import { SelectDataSource } from '../SelectDataSource/SelectDataSource';
import { IScheduleClassNames, IScheduleStyles, scheduleStylesDefault } from './Schedule.styles';

const minuteOptions: IDropdownOption[] = [
  { key: '*', text: 'Every minute' },
  { key: '*/2', text: 'Every even minutes' },
  { key: '1-59/2', text: 'Every odd minutes' },
  { key: '*/5', text: 'Every 5 minutes' },
  { key: '*/10', text: 'Every 10 minutes' },
  { key: '*/15', text: 'Every 15 minutes' },
  { key: '*/30', text: 'Every 30 minutes' },
];

const hourOptions: IDropdownOption[] = [
  { key: '*', text: 'Every hour' },
  { key: '*/2', text: 'Every even hour' },
  { key: '1-11/2', text: 'Every odd hour' },
  { key: '*/3', text: 'Every 3 hour' },
  { key: '*/4', text: 'Every 4 hour' },
  { key: '*/6', text: 'Every 6 hour' },
];

const daysOptions: IDropdownOption[] = [
  { key: '*', text: 'Every day of month' },
  { key: '*/2', text: 'Even day of month' },
  { key: '1-31/2', text: 'Odd day of month' },
  { key: '*/5', text: 'Every 5 days of month (5,10,15...)' },
  { key: '*/10', text: 'Every 10 days of month (10,20,30...)' },
];

const monthOptions: IDropdownOption[] = [
  { key: '*', text: 'Every month' },
  { key: '*/2', text: 'Even months' },
  { key: '1-11/2', text: 'Odd months' },
  { key: '*/4', text: 'Every 4 months' },
  { key: '*/6', text: 'Every 6 months(half year)' },
  { key: '1', text: 'Jan', data: { multiSelect: true } },
  { key: '2', text: 'Feb', data: { multiSelect: true } },
  { key: '3', text: 'Mar', data: { multiSelect: true } },
  { key: '4', text: 'Apr', data: { multiSelect: true } },
  { key: '5', text: 'May', data: { multiSelect: true } },
  { key: '6', text: 'Jun', data: { multiSelect: true } },
  { key: '7', text: 'Jul', data: { multiSelect: true } },
  { key: '8', text: 'Aug', data: { multiSelect: true } },
  { key: '9', text: 'Sep', data: { multiSelect: true } },
  { key: '10', text: 'Oct', data: { multiSelect: true } },
  { key: '11', text: 'Nov', data: { multiSelect: true } },
  { key: '12', text: 'Dec', data: { multiSelect: true } },
];

const weekdayOptions: IDropdownOption[] = [
  { key: '*', text: 'Every weekday' },
  { key: 'MON-FRI', text: 'Weekdays (MON-FRI)' },
  { key: 'SAT,SUN', text: 'Weekend' },
  { key: 'SUN', text: 'SUN', data: { multiSelect: true } },
  { key: 'MON', text: 'MON', data: { multiSelect: true } },
  { key: 'TUE', text: 'TUE', data: { multiSelect: true } },
  { key: 'WED', text: 'WED', data: { multiSelect: true } },
  { key: 'THU', text: 'THU', data: { multiSelect: true } },
  { key: 'FRI', text: 'FRI', data: { multiSelect: true } },
  { key: 'SAT', text: 'MON', data: { multiSelect: true } },
];

const getSingleSelectionKeys = (options: IDropdownOption[]) => {
  return options.reduce((keys: string[], option) => {
    if (!option.data) {
      keys.push(option.key.toString());
    }
    return keys;
  }, []);
};

const singleSelectionOptionSet = new Set([
  ...getSingleSelectionKeys(minuteOptions),
  ...getSingleSelectionKeys(hourOptions),
  ...getSingleSelectionKeys(daysOptions),
  ...getSingleSelectionKeys(monthOptions),
  ...getSingleSelectionKeys(weekdayOptions),
]);

enum scheduleUnit {
  Minutes = 'Minutes',
  Hours = 'Hours',
  DaysOfMonth = 'Days of Month',
  Months = 'Months',
  Weekday = 'weekdays',
}

const getUnits = () => {
  return Object.values(scheduleUnit);
};

const getOptions = (unit: scheduleUnit) => {
  let count = 0;
  let options: IDropdownOption[] = [];
  switch (unit) {
    case scheduleUnit.Minutes:
      count = 60;
      options = minuteOptions;
      break;
    case scheduleUnit.Hours:
      count = 24;
      options = hourOptions;
      break;
    case scheduleUnit.DaysOfMonth:
      count = 32;
      options = daysOptions;
      break;
    case scheduleUnit.Months:
      options = monthOptions;
      break;
    case scheduleUnit.Weekday:
      options = weekdayOptions;
      break;
  }
  options = [...options];
  if (count) {
    for (let i = 0; i < count; i++) {
      let text = i.toString();
      if (unit === scheduleUnit.Hours) {
        if (i === 0) {
          text = 'Midnight';
        } else if (i < 12) {
          text = `${text} am`;
        } else if (i === 12) {
          text = 'Noon';
        } else {
          text = `${text} pm`;
        }
      }
      if (unit === scheduleUnit.DaysOfMonth) {
        if (i !== 0) {
          options.push({ key: i.toString(), text, data: { multiSelect: true } });
        }
      } else {
        options.push({ key: i.toString(), text, data: { multiSelect: true } });
      }
    }
  }
  return [...options];
};

// Feature Store uses `* * * * *` cron 5 format. It doesn't use seconds and years.
const getCronString = (cron: string[][]) =>
  cron.map((d, i) => (d.length === 0 ? (i < 2 ? '0' : '*') : d.join(','))).join(' ');

const getCronArray = (cron = '0 0 * * *') => {
  return cron
    .trim()
    .split(' ')
    .map((d) => d.split(','));
};

const updateCronArray = (i: number, option: IDropdownOption, cron: string[][]) => {
  const key = option.key.toString();
  const selected = option.selected;
  const newCron = [...cron];
  if (selected) {
    // single selection
    if (singleSelectionOptionSet.has(key)) {
      newCron[i] = [key];
    } else {
      // if the existing value is single selection, replace it with a new value
      if (singleSelectionOptionSet.has(newCron[i][0])) {
        newCron[i] = [key];
      } else {
        // multiple selection
        newCron[i].push(key);
      }
    }
  } else {
    newCron[i] = newCron[i].filter((s) => s !== key);
  }
  return newCron;
};

interface IScheduleProps {
  featureSetVersion?: string;
  featureSetId?: string;
  projectId?: string;
  task?: ScheduledTask;
  onSave: (newTask?: ScheduledTask, oldTask?: ScheduledTask) => void;
  onCancel: () => void;
}

export const Schedule = ({ task, projectId, featureSetVersion, featureSetId, onSave, onCancel }: IScheduleProps) => {
  const [cron, setCron] = useState<string[][]>(getCronArray(task?.schedule));
  const [values, setValues] = useState<ScheduleTaskRequest>({
    projectId,
    featureSetId,
    featureSetVersion,
    description: task?.description,
  });
  const { addToast } = useToast();
  const onUpdate = useCallback(
    async (values: ScheduleTaskRequest) => {
      try {
        const schedule = getCronString(cron);
        if (task) {
          const { description } = values;
          const updatedFields = [];
          const scheduledTaskUpdateRequest: ScheduledTaskUpdateRequest = { scheduledTaskId: task.id };
          if (description !== task.description) {
            updatedFields.push('description');
            scheduledTaskUpdateRequest.description = description;
          }
          if (schedule !== task.schedule) {
            updatedFields.push('schedule');
            scheduledTaskUpdateRequest.schedule = schedule;
          }
          scheduledTaskUpdateRequest.updatedFields = updatedFields;
          await fsApi(CoreService.UpdateScheduledTask, scheduledTaskUpdateRequest);
          onSave(scheduledTaskUpdateRequest, task);
        } else {
          const { task }: ScheduledTaskResponse = await fsApi(CoreService.ScheduleIngestJob, { ...values, schedule });
          onSave(task);
        }
        addToast({
          messageBarType: MessageBarType.success,
          message: `The schedule has been ${task ? 'updated' : 'created'} successfully!`,
        });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while starting schedule job: `));
        onCancel();
      }
    },
    [cron]
  );
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      onUpdate(values);
      e.preventDefault();
    },
    [values, onUpdate]
  );
  useEffect(() => {
    setCron(getCronArray(task?.schedule));
    setValues({ ...values, description: task?.description });
  }, [task]);
  const classNames = useClassNames<IScheduleStyles, IScheduleClassNames>('Schedule', scheduleStylesDefault);
  return (
    <div className={classNames.root} data-test="schedule">
      <form className={classNames.form} onSubmit={onSubmit}>
        <div className={classNames.fields}>
          {!task && (
            <TextField
              name="name"
              label="Name"
              required
              onChange={(_e, name) => {
                setValues({ ...values, name });
              }}
            />
          )}
          <TextField
            name="description"
            label="Description"
            value={values.description}
            multiline
            rows={3}
            onChange={(_e, description) => {
              setValues({ ...values, description });
            }}
          />
          <div className={classNames.subTitle}>Schedule</div>
          {getUnits().map((unit, i) => {
            return (
              <div className={classNames.cron} key={unit}>
                <div>{unit}</div>
                <div>
                  <Dropdown
                    placeholder={`Select ${unit}`}
                    multiSelect
                    selectedKeys={cron[i]}
                    options={getOptions(unit)}
                    onChange={(_e, option) => {
                      if (option) {
                        const newCron = updateCronArray(i, option, cron);
                        setCron(newCron);
                      }
                    }}
                  />
                </div>
              </div>
            );
          })}
          <div className={classNames.cron}>
            <div>CRON</div>
            <div style={{ lineHeight: '32px' }}>{getCronString(cron)}</div>
          </div>
          {!task && (
            <SelectDataSource
              onChange={({ cred, dataSource: source }) => {
                setValues({ ...values, source, cred });
              }}
            />
          )}
        </div>
        <div className={classNames.buttons}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={onCancel} />
          <Button
            styles={[buttonStylesPrimary, buttonStylesPrimaryPanel]}
            type="submit"
            text={ContentsTitle.schedule}
            disabled={!task && !values.name}
          />
        </div>
      </form>
    </div>
  );
};
